import { SubmitHandler, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { CreateCompanyTaxRateDto } from "../../../../../../models/tax-rate.dto";
import {
	APP_CONFIG,
	formatRate,
	IAddEditTableForm,
	moneyFormat,
} from "../../../../../../helpers/appHelpers";
import useEditFormStore from "../../../../../../state-management/useEditFormStore";
import {
	useCreateCompanyTaxRate,
	useUpdateCompanyTaxRate,
} from "../../../../../../services/company-tax-rate.service";
import ModalComponent from "../../../../../../components/modals/ModalFormComponent";
import Label from "../../../../../../components/form_fields/Label";
import MoneyInput from "../../../../../../components/form_fields/MoneyInput";
import { useGetCompanyBenefitTax } from "../../../../../../services/company-benefit-tax.service";
import { useManageTableWithSearchParamsData } from "../../../../../../helpers/useManageTable";
import RateComponent from "../../../../../../components/form_fields/RateComponent";
import APISelectField from "../../../../../../components/form_fields/APISelectField";
import { PATHS } from "../../../../../../routes/routes.paths";

type IFormInputs = Omit<CreateCompanyTaxRateDto, "onDirectIncome"> & {
	onDirectIncome: string;
};

type ICompanyRateTaxRuleModalForm = IAddEditTableForm & {
	companyId: number;
	reloadTableData?: () => void;
};

const CompanyRateTaxRuleModalForm = ({
	open,
	setOpen,
	title,
	isEditForm,
	tableAPIRef,
	companyId,
	reloadTableData,
}: ICompanyRateTaxRuleModalForm) => {
	const { formDataValues } = useEditFormStore();
	const [onDirectIncome, setOnDirectIncome] = useState(false);
	const [uploading, setUploading] = useState(false);

	useEffect(() => {
		if (isEditForm) setOnDirectIncome(formDataValues?.onDirectIncome);
	}, [formDataValues]);

	const {
		register,
		watch,
		handleSubmit,
		setValue,
		reset,
		formState: { errors },
	} = useForm<IFormInputs>({
		defaultValues: {
			minTaxableAmount: isEditForm ? formDataValues?.minTaxableAmount : "",
			maxTaxableAmount: isEditForm ? formDataValues?.maxTaxableAmount : "",
			rateComponent: isEditForm
				? ((Number(formDataValues?.rateComponent) * 100) as any)
				: "",
			fixedComponent: isEditForm ? formDataValues?.fixedComponent : "",
			benefitTaxId: isEditForm ? formDataValues?.benefitTaxId : "",
			incomePeriod: isEditForm ? formDataValues?.incomePeriod : "",
			onDirectIncome: isEditForm
				? formDataValues?.onDirectIncome
					? "yes"
					: "no"
				: "",
		},
	});

	const maxTaxableAmount = watch("maxTaxableAmount");

	const { data: benefitTaxRules } = useGetCompanyBenefitTax(companyId, {
		limit: APP_CONFIG.PAGE_LIMIT,
		page: 1,
		orderBy: "createdAt:desc" as any,
	});

	const { mutate: createTaxRate } = useCreateCompanyTaxRate();
	const { mutate: updateTaxRate } = useUpdateCompanyTaxRate();
	const { addNewTableData } = useManageTableWithSearchParamsData();

	const onSubmit: SubmitHandler<IFormInputs> = (data: any) => {
		setUploading(true);

		formatRate(data);
		data.onDirectIncome = data.onDirectIncome === "yes";
		!data.benefitTaxId && delete data.benefitTaxId;
		!data.fixedComponent && delete data.fixedComponent;

		if (isEditForm) {
			if (data.benefitTaxId === formDataValues?.benefitTaxId) {
				delete data.benefitTaxId;
			}

			updateTaxRate(
				{ companyId, id: formDataValues?.id, payload: data },
				{
					onSuccess: (updatedData) => {
						try {
							tableAPIRef?.current.updateRows([updatedData.data]);
						} catch (error) {
							console.error("Error updating table rows:", error);
							reloadTableData?.();
						}
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		} else {
			createTaxRate(
				{ companyId, payload: data },
				{
					onSuccess: () => {
						addNewTableData();
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		}
	};

	const handleClose = () => {
		setOpen(false);
		reset();
	};

	return (
		<ModalComponent
			width={775}
			open={open}
			title={title}
			handleClose={handleClose}
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="grid grid-cols-2 gap-x-4 gap-y-6">
					<div className="w-full">
						<Label title="Income Period" for="incomePeriod" />
						<select
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm mb-2 ${
								errors.incomePeriod
									? "border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
							{...register("incomePeriod", {
								required: "Select income period here",
							})}
						>
							<option value="">Select Period</option>
							<option value="MONTH">Month</option>
							<option value="YEAR">Year</option>
						</select>
						{errors.incomePeriod && (
							<p className="text-red-500 text-sm mt-1">
								{errors.incomePeriod.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Min Taxable Amount" for="minTaxableAmount" />
						<MoneyInput
							attributes={{ id: "minTaxableAmount", placeholder: "0" }}
							register={register}
							name="minTaxableAmount"
							setValue={setValue}
							defaultValue={
								isEditForm ? formDataValues?.minTaxableAmount : undefined
							}
							required="Enter minimum taxable amount"
							validate={(value: number) => {
								if (maxTaxableAmount && value > maxTaxableAmount) {
									return "Cannot be more than the max taxable value";
								}
								return true;
							}}
							error={errors.minTaxableAmount}
						/>
						{errors.minTaxableAmount && (
							<p className="text-red-500 text-sm mt-1">
								{errors.minTaxableAmount.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Max Taxable Amount" for="maxTaxableAmount" />
						<MoneyInput
							attributes={{ id: "maxTaxableAmount", placeholder: "0" }}
							register={register}
							name="maxTaxableAmount"
							setValue={setValue}
							defaultValue={
								isEditForm ? formDataValues?.maxTaxableAmount : undefined
							}
							required="Enter maximum taxable amount"
							error={errors.maxTaxableAmount}
						/>
						{errors.maxTaxableAmount && (
							<p className="text-red-500 text-sm mt-1">
								{errors.maxTaxableAmount.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Rate Component" for="rateComponent" />
						<RateComponent
							attributes={{ id: "rateComponent" }}
							register={register}
							name="rateComponent"
							required="Enter rate component"
							error={errors.rateComponent}
						/>
						{errors.rateComponent && (
							<p className="text-red-500 text-sm mt-1">
								{errors.rateComponent.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Fixed Component" for="fixedComponent" optional />
						<MoneyInput
							attributes={{ id: "fixedComponent", placeholder: "0" }}
							register={register}
							name="fixedComponent"
							setValue={setValue}
							defaultValue={
								isEditForm ? formDataValues?.fixedComponent : undefined
							}
							required={false}
							error={errors.fixedComponent}
						/>
					</div>
					<div className="col-span-2">
						<div className="flex space-x-10 text-base">
							<Label title="Apply On Direct Income" for="onDirectIncome" />
							<div className="flex space-x-3 items-center">
								<input
									{...register("onDirectIncome", { required: true })}
									defaultChecked={
										isEditForm && formDataValues?.onDirectIncome === true
									}
									type="radio"
									value="yes"
									id="onDirectIncome"
									onChange={() => setOnDirectIncome(true)}
								/>
								<label htmlFor="onDirectIncome">Yes</label>
							</div>
							<div className="flex space-x-3 items-center">
								<input
									{...register("onDirectIncome", { required: true })}
									defaultChecked={
										isEditForm && formDataValues?.onDirectIncome === false
									}
									type="radio"
									value="no"
									id="notDirectIncome"
									onChange={() => setOnDirectIncome(false)}
								/>
								<label htmlFor="notDirectIncome">No</label>
							</div>
						</div>
					</div>
					{!onDirectIncome && (
						<div className="w-full">
							<Label title="Benefit Tax Rule" for="benefitTaxId" optional />
							<APISelectField
								key="benefitTaxId"
								attributes={{ id: "benefitTaxId" }}
								placeholder="Select Rule"
								register={register}
								name="benefitTaxId"
								customMapText={(option) =>
									`${option?.id}: [Min tax (${moneyFormat(option?.minTaxableAmount)}), Max tax (${moneyFormat(option?.maxTaxableAmount)}), Rate (${option?.rateComponent * 100}%)]`
								}
								options={benefitTaxRules?.data || []}
								pathname={
									PATHS.SUB_COMPANY_SETTINGS.TAX_RULES + "&taxRule=benefit"
								}
								required={false}
								error={errors.benefitTaxId}
								valueAsNumber
							/>
						</div>
					)}
				</div>

				<div className="w-full flex justify-end mt-8">
					<button
						type="submit"
						disabled={uploading}
						className="bg-primary-mango text-white h-14 px-8 rounded-lg text-sm"
					>
						{uploading
							? "Uploading..."
							: isEditForm
								? "Update Rate Rule"
								: "Add Rate Rule"}
					</button>
				</div>
			</form>
		</ModalComponent>
	);
};

export default CompanyRateTaxRuleModalForm;
